// src/pages/Reports/IssuanceReporting/IssuanceStock/StockTab.tsx
// RPT-004 (README.md mục 7) — tồn kho vé tại kho tổng (hệ thống chỉ có 1 kho, không multi-warehouse).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Input, Label, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { getTicketStockReportApi } from 'helpers/fakebackend_helper';
import { ISSUANCE_REPORTS_BASE } from 'helpers/url_helper';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { cleanParams, formatDateTime, formatQty, formatCurrency, getPaginatedPayload } from '../shared/formatters';
import { downloadReportExport } from '../shared/exportReport';
import { INITIAL_REPORT_PAGINATION, ReportPaginationState, TicketStockReportRow } from '../shared/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

const StockTab: React.FC = () => {
  const [planId, setPlanId] = useState('');
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'Xlsx' | 'Csv' | null>(null);
  const [rows, setRows] = useState<TicketStockReportRow[]>([]);
  const [pagination, setPagination] = useState<ReportPaginationState>(INITIAL_REPORT_PAGINATION);

  const buildFilterParams = useCallback(
    () => ({ IssuancePlanId: planId || undefined, Symbol: symbol || undefined }),
    [planId, symbol],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTicketStockReportApi(
        cleanParams({ ...buildFilterParams(), PageIndex: pagination.currentPage, PageSize: pagination.pageSize }),
      );
      const { items, totalItems, totalPages } = getPaginatedPayload(res);
      setRows(items);
      setPagination((prev) => ({ ...prev, totalItems, totalPages }));
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_bao_cao_ton_message'));
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams, pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, planId, symbol]);

  const handleFilterChange = (setter: (value: string) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleExport = async (format: 'Xlsx' | 'Csv') => {
    setExporting(format);
    try {
      await downloadReportExport(`${ISSUANCE_REPORTS_BASE}/Stock`, buildFilterParams(), format, 'TonKhoVe');
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_xuat_bao_cao_message'));
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Row className="g-3 mb-3 align-items-end">
        <Col md={4}>
          <Label className="form-label">{st('ky_phat_hanh_label')}</Label>
          <IssuancePlanSelect value={planId} onChange={(v) => { setPlanId(v); setPagination((p) => ({ ...p, currentPage: 1 })); }} />
        </Col>
        <Col md={4}>
          <Label className="form-label">{st('ky_hieu_label')}</Label>
          <Input type="text" placeholder={st('vd_n_b_placeholder')} value={symbol} onChange={handleFilterChange(setSymbol)} />
        </Col>
        <Col md={4} className="d-flex gap-2 justify-content-end">
          <Button color="success" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Xlsx')}>
            {exporting === 'Xlsx' ? <Spinner size="sm" /> : <i className="ri-file-excel-2-line align-bottom me-1"></i>}
            {st('excel_button')}
          </Button>
          <Button color="secondary" outline size="sm" disabled={!!exporting} onClick={() => handleExport('Csv')}>
            {exporting === 'Csv' ? <Spinner size="sm" /> : <i className="ri-file-text-line align-bottom me-1"></i>}
            {st('csv_button')}
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : (
        <>
          <DataTableShell
            headerRow={
              <tr>
                <th className="text-center" style={{ width: 50 }}>{st('stt_column')}</th>
                <th>{st('ky_phat_hanh_label')}</th>
                <th>{st('loai_ve_column')}</th>
                <th>{st('ky_hieu_label')}</th>
                <th>{st('kho_column')}</th>
                <th className="text-end">{st('so_luong_ton_column')}</th>
                <th className="text-end">{st('gia_tri_ton_column')}</th>
                <th>{st('cap_nhat_gan_nhat_column')}</th>
              </tr>
            }
          >
            {rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted py-5">{st('khong_co_du_lieu_text')}</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.issuancePlanId}-${row.symbol}`}>
                  <td className="text-center">{getSttNumber(pagination.currentPage, pagination.pageSize, index)}</td>
                  <td>{row.planCode}</td>
                  <td>{row.ticketTypeName || row.ticketType}</td>
                  <td>{row.symbol}</td>
                  <td>{row.warehouseLabel}</td>
                  <td className="text-end">{formatQty(row.quantity)}</td>
                  <td className="text-end">{formatCurrency(row.value)}</td>
                  <td>{formatDateTime(row.lastUpdatedAt)}</td>
                </tr>
              ))
            )}
          </DataTableShell>

          <AgentPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, currentPage: page }))}
          />
        </>
      )}
    </>
  );
};

export default StockTab;
